import axios, { AxiosError, type AxiosRequestConfig } from 'axios'
import { readSession, writeSession, type AuthSession } from './session'

export interface CommonResult<T> {
  code: number
  data: T
  msg?: string
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code?: number,
    public readonly status?: number,
  ) {
    super(message)
  }
}

interface RefreshTokenResponse {
  accessToken?: string
  refreshToken?: string
  expiresTime?: string
}

const API_BASE = String(import.meta.env.VITE_API_BASE || '/admin-api')

export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 25_000,
})

apiClient.interceptors.request.use((config) => {
  const session = readSession()
  if (session) {
    config.headers.Authorization = `Bearer ${session.accessToken}`
    config.headers['tenant-id'] = session.tenantId
  }
  const storeId = sessionStorage.getItem('leshe-admin-store-id')
  if (storeId) config.headers['X-Store-Id'] = storeId
  return config
})

let refreshPromise: Promise<AuthSession | null> | null = null

type RetriableRequestConfig = AxiosRequestConfig & { _retried?: boolean }

function expireSession() {
  if (!readSession()) return
  writeSession(null)
  window.dispatchEvent(new Event('leshe:session-expired'))
}

async function refreshSession() {
  const session = readSession()
  if (!session?.refreshToken) return null
  try {
    const response = await axios.post<CommonResult<RefreshTokenResponse>>(
      `${API_BASE}/system/auth/refresh-token`,
      null,
      { params: { refreshToken: session.refreshToken }, headers: { 'tenant-id': session.tenantId } },
    )
    if (response.data.code !== 0 || !response.data.data?.accessToken) return null
    const next: AuthSession = {
      ...session,
      accessToken: String(response.data.data.accessToken),
      refreshToken: String(response.data.data.refreshToken || session.refreshToken),
      expiresTime: response.data.data.expiresTime,
    }
    writeSession(next)
    return next
  } catch {
    return null
  }
}

async function retryAfterUnauthorized(config?: RetriableRequestConfig) {
  if (!config || !readSession()) return null
  if (config._retried) {
    expireSession()
    return null
  }

  config._retried = true
  refreshPromise ??= refreshSession().finally(() => { refreshPromise = null })
  const refreshed = await refreshPromise
  if (!refreshed) {
    expireSession()
    return null
  }

  config.headers = { ...config.headers, Authorization: `Bearer ${refreshed.accessToken}` }
  return apiClient.request(config)
}

apiClient.interceptors.response.use(
  async (response) => {
    const result = response.data as Partial<CommonResult<unknown>> | undefined
    if (result?.code === 401) {
      return await retryAfterUnauthorized(response.config) ?? response
    }
    return response
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const retried = await retryAfterUnauthorized(error.config as RetriableRequestConfig | undefined)
      if (retried) return retried
    }
    throw error
  },
)

export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await apiClient.request<CommonResult<T>>(config)
    const result = response.data
    if (result.code !== 0) throw new ApiError(result.msg || '请求失败', result.code, response.status)
    return result.data
  } catch (error) {
    if (error instanceof ApiError) throw error
    if (axios.isAxiosError<CommonResult<unknown>>(error)) {
      throw new ApiError(
        error.response?.data?.msg || error.message || '网络连接失败',
        error.response?.data?.code,
        error.response?.status,
      )
    }
    throw error
  }
}

export async function download(
  path: string,
  params: Record<string, unknown> | URLSearchParams,
  filename: string,
  storeId?: string | null,
) {
  const response = await apiClient.get<Blob>(path, {
    params,
    responseType: 'blob',
    headers: storeId ? { 'X-Store-Id': storeId } : undefined,
  })
  const url = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function withStore<T extends AxiosRequestConfig>(config: T, storeId?: string | null): T {
  return {
    ...config,
    headers: { ...config.headers, ...(storeId ? { 'X-Store-Id': storeId } : {}) },
  }
}
